import React, { useContext } from 'react';
import { Box, Paper, PaperProps, Typography, useTheme } from '@mui/material';
import { UploadedFile } from 'shared/lib/components/controls/uploadArea/uploadArea';
import IconTrash from 'shared/icons/trash';
import { UiContext } from 'shared/lib/contexts/uiContext';
import {
  getFormatIcon,
  getFormatNameFromFile,
  noFormatName,
  previewFormats,
  sizeToString,
} from 'shared/lib/utils/utils';
import IconOk from 'shared/icons/iconOk';
import UploadPreviewText, {
  UploadPreviewTextProps,
} from 'pages/uploadPage/uploadFiles/fileItem/uploadPreviewText';
import IconWarningSign from 'shared/icons/iconWarningSign';
import LimitedTypography from 'shared/lib/components/text/limitedTypography';
import { BackErrors } from 'shared/lib/utils/globalUtils';
import { nbsp } from 'shared/lib/utils/uiConstants';
import ErrorNotification from 'shared/lib/components/lilUi/errorNotification';

export default function FileItem({
  file,
  preview,
  onDelete,
  onSetPreview,
  error,
  ...other
}: PaperProps & {
  file: UploadedFile;
  onDelete: () => void;
  error?: string;
} & UploadPreviewTextProps) {
  const {
    spacings: { sp },
  } = useContext(UiContext);
  const theme = useTheme();

  const format = getFormatNameFromFile(file.originalFile.name);
  const formatIcon = getFormatIcon({ format, iconProps: { sizeKey: '16x16' } });

  const errorMessage = error
    ? error === BackErrors.FileExists
      ? `Загрузка запрещена. Обнаружен файл с${nbsp}таким${nbsp}же наименованием. Переименуйте или удалите один из${nbsp}файлов и${nbsp}попробуйте снова`
      : 'Не удалось загрузить'
    : '';

  return (
    <Paper
      elevation={10}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        border: 1,
        borderColor: 'text.secondary',
        px: sp.s30,
        py: sp.s40,
        gap: sp.s20,
        ...other.sx,
      }}
      {...other}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="f18">{file.originalFile.name}</Typography>
        <IconTrash
          onClick={onDelete}
          extColor={theme.palette.error.main}
          sizeKey="18x18"
        ></IconTrash>
      </Box>
      {errorMessage ? (
        <ErrorNotification error={errorMessage}></ErrorNotification>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', gap: sp.s30 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {formatIcon}
              <Typography variant="f14">{format || noFormatName}</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Typography variant="f14">
                {sizeToString(file.originalFile.size || 0)}
              </Typography>
              <IconOk extColor={theme.palette.success.main}></IconOk>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            {previewFormats.includes(String(format)) ? (
              ''
            ) : (
              <UploadPreviewText
                preview={preview}
                onSetPreview={onSetPreview}
              ></UploadPreviewText>
            )}
          </Box>
        </Box>
      )}
    </Paper>
  );
}
